import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Github,
  GitCommit,
  Users,
  Activity,
  Archive,
  Search,
  X,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  GitBranch,
  Code2,
  Calendar,
  Clock,
  CheckCircle2,
  Loader2,
  Eye,
} from 'lucide-react';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';
import CollegeFooter from './CollegeFooter';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

/* ── helpers ── */
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};

const fmtRelative = (d) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const fmtNumber = (n) => {
  if (n == null) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return n.toLocaleString();
  return String(n);
};

const initials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).map((w) => w[0]?.toUpperCase()).slice(0, 2).join('');
};

const AVATAR_COLORS = [
  '#6366F1', '#7C3AED', '#059669', '#D97706', '#DC2626',
  '#0EA5E9', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6',
];
const avatarColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

/* ── Reusable sub-components ── */

const SummaryCard = ({ icon: Icon, label, value, description, color, loading }) => (
  <div
    className="bg-white rounded-2xl p-5 flex items-start gap-4"
    style={{ border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-xs)' }}
  >
    <div
      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: `${color}18` }}
    >
      <Icon size={20} style={{ color }} />
    </div>
    <div className="min-w-0 flex-1">
      {loading ? (
        <>
          <div className="h-7 w-16 bg-gray-100 rounded-lg animate-pulse mb-1.5" />
          <div className="h-3.5 w-32 bg-gray-100 rounded animate-pulse mb-1" />
          <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
        </>
      ) : (
        <>
          <div className="text-2xl font-bold text-gray-900 leading-none tracking-tight">{fmtNumber(value)}</div>
          <div className="text-sm font-semibold text-gray-700 mt-1">{label}</div>
          <div className="text-xs text-gray-400 mt-0.5">{description}</div>
        </>
      )}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const active = status === 'active';
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={
        active
          ? { color: '#059669', background: '#D1FAE5' }
          : { color: '#6B7280', background: '#F3F4F6' }
      }
    >
      <span
        className="w-1.5 h-1.5 rounded-full inline-block"
        style={{ background: active ? '#059669' : '#9CA3AF' }}
      />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

const CommitBadge = ({ count }) => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
    style={{ background: '#EEF2FF', color: '#4F46E5' }}
  >
    <GitCommit size={10} />
    {fmtNumber(count)}
  </span>
);

/* ── Table row skeleton ── */
const SkeletonRow = () => (
  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
    {[40, 100, 90, 110, 130, 60, 70, 64, 80].map((w, i) => (
      <td key={i} className="px-4 py-3.5">
        <div
          className="h-4 bg-gray-100 rounded animate-pulse"
          style={{ width: w, maxWidth: '100%' }}
        />
      </td>
    ))}
  </tr>
);

/* ── Activity Drawer ── */
const ActivityDrawer = ({ open, onClose, studentId, studentName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [data, setData]       = useState(null);

  useEffect(() => {
    if (!open || !studentId) return;
    setLoading(true);
    setError('');
    setData(null);
    axios
      .get(`${API_BASE_URL}/api/github/student-repos/${studentId}/commits`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.error || err.message || 'Failed to load activity'))
      .finally(() => setLoading(false));
  }, [open, studentId]);

  if (!open) return null;

  const { commits = [], repoMeta = {} } = data || {};

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[150]"
        style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Activity for ${studentName}`}
        className="fixed inset-y-0 right-0 z-[200] w-full max-w-md bg-white flex flex-col"
        style={{ boxShadow: '-8px 0 40px rgba(0,0,0,0.12)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--border-subtle)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#EEF2FF' }}>
              <Activity size={16} style={{ color: '#4F46E5' }} />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-gray-900 text-sm leading-none">Repository Activity</h2>
              <p className="text-xs text-gray-500 mt-0.5 truncate">{studentName || 'Student'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex flex-col gap-3 p-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <AlertCircle size={32} className="text-red-400 mb-3" />
              <p className="text-sm font-semibold text-gray-700 mb-1">Failed to load activity</p>
              <p className="text-xs text-gray-500">{error}</p>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Repo meta */}
              <div className="p-5" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <div className="grid grid-cols-2 gap-4">
                  <MetaItem icon={Github} label="Repository" value={repoMeta.repoName || '—'} mono />
                  <MetaItem icon={GitBranch} label="Default Branch" value={repoMeta.defaultBranch || 'main'} />
                  <MetaItem icon={Code2} label="Language" value={repoMeta.language || 'Unknown'} />
                  <MetaItem icon={Calendar} label="Created" value={fmtDate(repoMeta.createdAt)} />
                  <MetaItem icon={Clock} label="Last Commit" value={fmtDate(commits[0]?.date)} />
                  {repoMeta.repoUrl && (
                    <div className="col-span-2">
                      <a
                        href={repoMeta.repoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline transition"
                        aria-label="Open repository on GitHub"
                      >
                        <ExternalLink size={11} />
                        Open on GitHub
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Commit timeline */}
              <div className="p-5">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">
                  Recent Commits ({commits.length})
                </h3>
                {commits.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No commits found.</p>
                ) : (
                  <ol className="relative" style={{ borderLeft: '2px solid #E0E7FF', paddingLeft: '1.25rem' }}>
                    {commits.map((c, idx) => (
                      <li key={c.sha + idx} className="mb-4 last:mb-0">
                        <span
                          className="absolute -left-[9px] w-4 h-4 rounded-full flex items-center justify-center"
                          style={{ background: '#EEF2FF', border: '2px solid #6366F1', top: 2 + idx * 52 }}
                          aria-hidden="true"
                        />
                        <div className="bg-gray-50 rounded-xl px-3 py-2.5 hover:bg-indigo-50 transition">
                          <p className="text-xs font-semibold text-gray-800 leading-snug mb-1 line-clamp-2">{c.message}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-indigo-500 font-mono">{c.sha}</span>
                            <span className="text-xs text-gray-400">{c.author}</span>
                            <span className="text-xs text-gray-400 ml-auto">{fmtRelative(c.date)}</span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const MetaItem = ({ icon: Icon, label, value, mono = false }) => (
  <div>
    <div className="flex items-center gap-1 mb-0.5">
      <Icon size={10} className="text-gray-400" />
      <span className="text-xs text-gray-400">{label}</span>
    </div>
    <span className={`text-xs font-semibold text-gray-800 ${mono ? 'font-mono' : ''}`}>{value || '—'}</span>
  </div>
);

/* ── Toast ── */
const Toast = ({ message }) => {
  if (!message) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-[300] px-4 py-3 bg-gray-900 text-white text-sm rounded-xl shadow-lg"
    >
      {message}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const CollegeGitHubDashboard = () => {
  const [collegeId, setCollegeId] = useState(null);

  /* Data */
  const [students,   setStudents]   = useState([]);
  const [summary,    setSummary]    = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

  /* Loading / error */
  const [loading,         setLoading]         = useState(false);
  const [loadingSync,     setLoadingSync]     = useState(false);
  const [loadingBackfill, setLoadingBackfill] = useState(false);
  const [error,           setError]           = useState('');
  const [toast,           setToast]           = useState('');

  /* Filters */
  const [searchStudent,  setSearchStudent]  = useState('');
  const [searchRepo,     setSearchRepo]     = useState('');
  const [searchUsername, setSearchUsername] = useState('');
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [sortBy,         setSortBy]         = useState('latest');
  const [currentPage,    setCurrentPage]    = useState(1);

  /* Activity drawer */
  const [drawerOpen,        setDrawerOpen]        = useState(false);
  const [drawerStudentId,   setDrawerStudentId]   = useState(null);
  const [drawerStudentName, setDrawerStudentName] = useState('');

  const searchTimer = useRef(null);

  /* ── init ── */
  useEffect(() => {
    const raw  = localStorage.getItem('user') || '{}';
    const user = JSON.parse(raw);
    const cid  = user?.id || user?.college_id;
    console.log('[GitHubDashboard] localStorage user raw:', raw);
    console.log('[GitHubDashboard] Resolved collegeId:', cid, '| user.id:', user?.id, '| user.college_id:', user?.college_id);
    if (cid) setCollegeId(cid);
    else console.warn('[GitHubDashboard] ⚠️ No collegeId found in localStorage — dashboard will not load');
  }, []);

  /* ── combined search string for API ── */
  const combinedSearch = [searchStudent, searchRepo, searchUsername].filter(Boolean).join(' ');

  /* ── fetch ── */
  const fetchDashboard = useCallback(async (cid, page = 1, search = '', status = 'all', sort = 'latest') => {
    if (!cid) return;
    setLoading(true);
    setError('');
    console.log('[GitHubDashboard] fetchDashboard called with:', { collegeId: cid, page, search, status, sort });
    try {
      const res = await axios.get(`${API_BASE_URL}/api/github/student-repos`, {
        params: { collegeId: cid, page, limit: 20, search, status, sort },
      });
      console.log('[GitHubDashboard] API response status:', res.status);
      console.log('[GitHubDashboard] API response data:', JSON.stringify(res.data, null, 2));
      const { students: rows, pagination: pag, summary: sum } = res.data;
      console.log('[GitHubDashboard] students count:', rows?.length, '| total:', pag?.total);
      if (!rows || rows.length === 0) {
        console.warn('[GitHubDashboard] ⚠️ No students returned. Check if student_github_accounts has rows with college_id =', cid);
      }
      setStudents(rows || []);
      setPagination(pag || { total: 0, page: 1, limit: 20, totalPages: 1 });
      if (sum) setSummary(sum);
    } catch (err) {
      console.error('[GitHubDashboard] fetchDashboard error:', err.response?.data || err.message);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (collegeId) fetchDashboard(collegeId, currentPage, combinedSearch, statusFilter, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collegeId, currentPage, statusFilter, sortBy]);

  /* Debounce search */
  useEffect(() => {
    if (!collegeId) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setCurrentPage(1);
      fetchDashboard(collegeId, 1, combinedSearch, statusFilter, sortBy);
    }, 350);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStudent, searchRepo, searchUsername]);

  /* ── sync stats ── */
  const handleSyncStats = async () => {
    if (!collegeId) return;
    setLoadingSync(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/github/sync-student-repo-stats`, { collegeId });
      showToast(`Synced ${res.data?.synced ?? 0} repositories.`);
      fetchDashboard(collegeId, currentPage, combinedSearch, statusFilter, sortBy);
    } catch (err) {
      showToast(err.response?.data?.error || 'Sync failed.');
    } finally {
      setLoadingSync(false);
    }
  };

  /* ── backfill missing student accounts ── */
  const handleBackfill = async () => {
    if (!collegeId) return;
    setLoadingBackfill(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/github/backfill-student-accounts`, { collegeId });
      const { inserted, found } = res.data;
      showToast(found === 0 ? 'All enrolled students are already synced.' : `Recovered ${inserted} of ${found} missing student(s).`);
      if (inserted > 0) fetchDashboard(collegeId, currentPage, combinedSearch, statusFilter, sortBy);
    } catch (err) {
      showToast(err.response?.data?.error || 'Backfill failed.');
    } finally {
      setLoadingBackfill(false);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const openActivityDrawer = (student) => {
    setDrawerStudentId(student.student_id);
    setDrawerStudentName(student.student_name || student.github_username || 'Student');
    setDrawerOpen(true);
  };

  const clearFilters = () => {
    setSearchStudent('');
    setSearchRepo('');
    setSearchUsername('');
    setStatusFilter('all');
    setSortBy('latest');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchStudent || searchRepo || searchUsername || statusFilter !== 'all' || sortBy !== 'latest';
  const isFirstLoad      = loading && students.length === 0 && !summary;
  const isEmpty          = !loading && students.length === 0 && !error;

  /* ── render ── */
  return (
    <div className="college-main-layout" style={{ background: '#F8FAFC' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content p-6" role="main">

          {/* ── Page header ── */}
          <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#EEF2FF' }}>
                <Github size={20} style={{ color: '#4F46E5' }} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 leading-tight">GitHub Dashboard</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Monitor student GitHub repositories, coding activity and repository health.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleBackfill}
                disabled={loadingBackfill || !collegeId}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 bg-white border hover:bg-gray-50 disabled:opacity-60 transition"
                style={{ border: '1px solid var(--border-subtle)' }}
                title="Recover enrolled students missing from the dashboard"
                aria-label="Recover missing student records"
              >
                <Users size={14} className={loadingBackfill ? 'animate-pulse' : ''} />
                {loadingBackfill ? 'Recovering...' : 'Recover Students'}
              </button>
              <button
                onClick={handleSyncStats}
                disabled={loadingSync || !collegeId}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 bg-white border hover:bg-gray-50 disabled:opacity-60 transition"
                style={{ border: '1px solid var(--border-subtle)' }}
                title="Sync commit statistics from GitHub"
                aria-label="Sync repository statistics"
              >
                <RefreshCw size={14} className={loadingSync ? 'animate-spin' : ''} />
                Sync Stats
              </button>
            </div>
          </div>

          {/* ── Summary cards ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard
              icon={Github}
              label="Total Student Repositories"
              value={summary?.totalRepos}
              description="Repositories created by students"
              color="#6366F1"
              loading={isFirstLoad}
            />
            <SummaryCard
              icon={Activity}
              label="Active Repositories"
              value={summary?.activeRepos}
              description="Repositories with recent activity"
              color="#059669"
              loading={isFirstLoad}
            />
            <SummaryCard
              icon={GitCommit}
              label="Total Commits"
              value={summary?.totalCommits}
              description="Commits across all student repositories"
              color="#7C3AED"
              loading={isFirstLoad}
            />
            <SummaryCard
              icon={Archive}
              label="Inactive Repositories"
              value={summary?.inactiveRepos}
              description="Repositories with no recent commits"
              color="#D97706"
              loading={isFirstLoad}
            />
          </div>

          {/* ── Error banner ── */}
          {error && (
            <div
              className="mb-5 p-4 rounded-xl flex items-center gap-3 text-sm text-red-700"
              style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }}
              role="alert"
            >
              <AlertCircle size={16} className="flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button
                onClick={() => fetchDashboard(collegeId, currentPage, combinedSearch, statusFilter, sortBy)}
                className="text-xs font-semibold underline hover:no-underline ml-2"
              >
                Retry
              </button>
            </div>
          )}

          {/* ── Main table card ── */}
          <div
            className="bg-white rounded-2xl overflow-hidden"
            style={{ border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-xs)' }}
          >
            {/* Filter bar */}
            <div
              className="px-5 py-4 flex flex-wrap gap-3 items-center"
              style={{ borderBottom: '1px solid var(--border-subtle)' }}
            >
              {/* Search Student */}
              <SearchInput
                placeholder="Search student…"
                value={searchStudent}
                onChange={setSearchStudent}
                aria-label="Search by student name"
              />
              {/* Search Repo */}
              <SearchInput
                placeholder="Search repository…"
                value={searchRepo}
                onChange={setSearchRepo}
                aria-label="Search by repository name"
              />
              {/* Search Username */}
              <SearchInput
                placeholder="GitHub username…"
                value={searchUsername}
                onChange={setSearchUsername}
                aria-label="Search by GitHub username"
              />

              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 rounded-xl text-sm font-medium text-gray-700 bg-white border focus:outline-none focus:ring-2 focus:ring-indigo-300 transition"
                style={{ border: '1px solid var(--border-subtle)' }}
                aria-label="Filter by status"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 rounded-xl text-sm font-medium text-gray-700 bg-white border focus:outline-none focus:ring-2 focus:ring-indigo-300 transition"
                style={{ border: '1px solid var(--border-subtle)' }}
                aria-label="Sort results"
              >
                <option value="latest">Latest Activity</option>
                <option value="commits">Most Commits</option>
                <option value="alphabetical">Alphabetical</option>
              </select>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800 transition ml-auto"
                  aria-label="Clear all filters"
                >
                  <X size={12} />
                  Clear
                </button>
              )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto" role="region" aria-label="Student repositories table">
              <table className="w-full text-sm" aria-label="Student GitHub repositories">
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--border-subtle)' }}>
                    <Th>Student</Th>
                    <Th>Student ID</Th>
                    <Th>GitHub Username</Th>
                    <Th>Repository</Th>
                    <Th>Repository URL</Th>
                    <Th align="right">Commits</Th>
                    <Th>Last Commit</Th>
                    <Th>Status</Th>
                    <Th align="right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(6)].map((_, i) => <SkeletonRow key={i} />)
                  ) : (
                    students.map((s) => (
                      <StudentRow
                        key={s.student_id}
                        student={s}
                        onViewActivity={() => openActivityDrawer(s)}
                      />
                    ))
                  )}
                </tbody>
              </table>

              {/* Empty state */}
              {isEmpty && (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                    style={{ background: '#EEF2FF' }}
                    aria-hidden="true"
                  >
                    <Github size={28} style={{ color: '#6366F1' }} />
                  </div>
                  <h3 className="text-base font-bold text-gray-800 mb-1">No GitHub Repositories Found</h3>
                  <p className="text-sm text-gray-400 max-w-sm mb-5">
                    {hasActiveFilters
                      ? 'No repositories match your current filters. Try adjusting the search or filter criteria.'
                      : 'Students will appear here once they create a GitHub repository from the Student Portal.'}
                  </p>
                  <button
                    onClick={() => fetchDashboard(collegeId, 1, '', 'all', 'latest')}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 bg-white border hover:bg-gray-50 transition"
                    style={{ border: '1px solid var(--border-subtle)' }}
                  >
                    <RefreshCw size={14} />
                    Refresh
                  </button>
                </div>
              )}
            </div>

            {/* Pagination */}
            {!loading && pagination.totalPages > 1 && (
              <Pagination
                pagination={pagination}
                currentPage={currentPage}
                onPage={(p) => setCurrentPage(p)}
              />
            )}

            {/* Row count */}
            {!loading && students.length > 0 && (
              <div
                className="px-5 py-3 flex items-center justify-between text-xs text-gray-400"
                style={{ borderTop: '1px solid var(--border-subtle)' }}
              >
                <span>
                  Showing{' '}
                  <strong className="text-gray-600">
                    {(currentPage - 1) * pagination.limit + 1}–{Math.min(currentPage * pagination.limit, pagination.total)}
                  </strong>{' '}
                  of <strong className="text-gray-600">{pagination.total}</strong> repositories
                </span>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Activity Drawer */}
      <ActivityDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        studentId={drawerStudentId}
        studentName={drawerStudentName}
      />

      <Toast message={toast} />
      <CollegeFooter />
    </div>
  );
};

/* ── Table helpers ── */

const Th = ({ children, align = 'left' }) => (
  <th
    scope="col"
    className={`text-xs font-semibold text-gray-500 px-4 py-3 whitespace-nowrap sticky top-0 ${align === 'right' ? 'text-right' : 'text-left'}`}
    style={{ background: '#F8FAFC' }}
  >
    {children}
  </th>
);

const StudentRow = ({ student, onViewActivity }) => {
  const [urlCopied, setUrlCopied] = useState(false);
  const color = avatarColor(student.student_name || student.github_username);
  const init  = initials(student.student_name || student.github_username);

  const copyUrl = () => {
    if (!student.repo_url) return;
    navigator.clipboard.writeText(student.repo_url).catch(() => {});
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  return (
    <tr
      style={{ borderBottom: '1px solid var(--border-subtle)' }}
      className="hover:bg-gray-50 transition-colors"
    >
      {/* Student */}
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
            style={{ background: color }}
            aria-hidden="true"
          >
            {init}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-gray-800 text-sm leading-none" title={student.student_name}>
              {student.student_name || '—'}
            </div>
            <div className="text-xs text-gray-400 mt-0.5" title={student.student_email}>
              {student.student_email || ''}
            </div>
          </div>
        </div>
      </td>

      {/* Student ID */}
      <td className="px-4 py-3.5">
        <span className="text-xs text-gray-500 font-mono" title={student.student_id}>
          {student.student_id || '—'}
        </span>
      </td>

      {/* GitHub Username */}
      <td className="px-4 py-3.5">
        {student.github_username ? (
          <a
            href={`https://github.com/${student.github_username}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline transition"
            aria-label={`GitHub profile of ${student.github_username}`}
          >
            <Github size={13} />
            {student.github_username}
          </a>
        ) : (
          <span className="text-xs text-gray-400">Not connected</span>
        )}
      </td>

      {/* Repo name */}
      <td className="px-4 py-3.5">
        <span
          className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-1 rounded-lg inline-block"
          title={student.repo_name}
        >
          {student.repo_name || '—'}
        </span>
      </td>

      {/* Repo URL */}
      <td className="px-4 py-3.5">
        {student.repo_url ? (
          <div className="flex items-center gap-1">
            <a
              href={student.repo_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-indigo-500 hover:text-indigo-700 hover:underline transition"
              title={student.repo_url}
              aria-label={`Open repository ${student.repo_name}`}
            >
              {student.repo_url.replace('https://', '')}
            </a>
            <button
              onClick={copyUrl}
              className="flex-shrink-0 p-0.5 rounded text-gray-300 hover:text-gray-600 transition"
              aria-label={urlCopied ? 'URL copied' : 'Copy URL'}
              title={urlCopied ? 'Copied!' : 'Copy URL'}
            >
              {urlCopied ? <CheckCircle2 size={11} style={{ color: '#059669' }} /> : <ExternalLink size={11} />}
            </button>
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>

      {/* Commits */}
      <td className="px-4 py-3.5 text-right">
        <CommitBadge count={student.commit_count || 0} />
      </td>

      {/* Last commit */}
      <td className="px-4 py-3.5">
        <span
          className="text-xs text-gray-600"
          title={student.last_commit_date ? new Date(student.last_commit_date).toLocaleString() : undefined}
        >
          {student.last_commit_date ? fmtRelative(student.last_commit_date) : '—'}
        </span>
      </td>

      {/* Status */}
      <td className="px-4 py-3.5">
        <StatusBadge status={student.repo_status} />
      </td>

      {/* Actions */}
      <td className="px-4 py-3.5">
        <div className="flex items-center justify-end gap-2">
          {student.repo_url && (
            <a
              href={student.repo_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition whitespace-nowrap"
              aria-label={`View repository ${student.repo_name}`}
            >
              <ExternalLink size={11} />
              View Repo
            </a>
          )}
          <button
            onClick={onViewActivity}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition whitespace-nowrap"
            aria-label={`View activity for ${student.student_name || student.github_username}`}
          >
            <Eye size={11} />
            Activity
          </button>
        </div>
      </td>
    </tr>
  );
};

/* ── Search input ── */
const SearchInput = ({ placeholder, value, onChange, 'aria-label': ariaLabel }) => (
  <div className="relative">
    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className="pl-8 pr-3 py-2 rounded-xl text-sm text-gray-700 bg-white border focus:outline-none focus:ring-2 focus:ring-indigo-300 transition w-40 placeholder-gray-400"
      style={{ border: '1px solid var(--border-subtle)' }}
    />
    {value && (
      <button
        onClick={() => onChange('')}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        aria-label={`Clear ${placeholder}`}
      >
        <X size={12} />
      </button>
    )}
  </div>
);

/* ── Pagination ── */
const Pagination = ({ pagination, currentPage, onPage }) => {
  const { totalPages, total, limit } = pagination;
  const pages = [];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('…');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('…');
    pages.push(totalPages);
  }

  return (
    <div
      className="px-5 py-3 flex items-center justify-between gap-2 flex-wrap"
      style={{ borderTop: '1px solid var(--border-subtle)' }}
      role="navigation"
      aria-label="Pagination"
    >
      <button
        onClick={() => onPage(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 bg-white border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
        style={{ border: '1px solid var(--border-subtle)' }}
        aria-label="Previous page"
      >
        <ChevronLeft size={13} /> Prev
      </button>

      <div className="flex items-center gap-1">
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`ellipsis-${i}`} className="px-2 text-xs text-gray-400">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPage(p)}
              aria-label={`Page ${p}`}
              aria-current={p === currentPage ? 'page' : undefined}
              className="w-7 h-7 rounded-lg text-xs font-semibold transition"
              style={
                p === currentPage
                  ? { background: '#4F46E5', color: '#fff' }
                  : { color: '#6B7280', background: 'transparent' }
              }
            >
              {p}
            </button>
          )
        )}
      </div>

      <button
        onClick={() => onPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 bg-white border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
        style={{ border: '1px solid var(--border-subtle)' }}
        aria-label="Next page"
      >
        Next <ChevronRight size={13} />
      </button>
    </div>
  );
};

export default CollegeGitHubDashboard;
